import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, DocumentModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { Checkbox, DataTableCell, DataTableRow, Icon, Link, Typography } from "@insite/mobius/index";
import { CustomIncludeModalConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { ItemWithHelpInfoHelp } from "@thkStore/Components/Tooltip/ItemWithHelpInfo";
import PortalTooltip from "@thkStore/Components/Tooltip/PortalTooltip";
import { DISCONTINUED_BUT_VISIBLE, HAS_INCLUDES_PRODUCTS, IS_CUSTOM_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import Book from "@thkStore/Icons/Book";
import Cad3D from "@thkStore/Icons/Cad3D";
import ProductQuantityOrdered from "@thkStore/Overrides/Components/ProductQuantityOrdered";
import { AttributeTypeNameValues } from "@thkStore/Types/AttributeTypeNameValues";
import CustomRailSetupButton from "@thkStore/Widgets/VariantChildrenGrid/CustomRailSetupButton";
import CustomDocumentModel from "@thkStore/Types/CustomDocumentModel";
import { DocumentNameValues, DocumentTypeValues } from "@thkStore/Types/DocumentTypeValues";
import { CustomVariantTraitModel } from "@thkStore/Types/VariantChildrenGridModel";
import { getVariantTraitValueForProduct, hideTrait } from "@thkStore/Widgets/VariantChildrenGrid/utils";
import { VariantChildrenGridContextModel } from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridContext";
import * as React from "react";
import styled from "styled-components";
import { ProductDetailsEnum } from "@thkStore/Common/Enums/ProductDetailsEnum";

const QuoteWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const QuoteRow = styled.div`
    display: flex;
    align-items: center;
`;

const sortByDate = (a: DocumentModel, b: DocumentModel) => {
    const dateA = new Date((a as CustomDocumentModel).createdOn).getTime();
    const dateB = new Date((b as CustomDocumentModel).createdOn).getTime();
    return dateB - dateA;
};

const RequiresQuoteCell: React.FC<{ unitOfMeasure?: string }> = ({ unitOfMeasure }) => (
    <QuoteWrapper>
        <QuoteRow>
            <Typography weight="bold" data-test-selector="quoteRequiredText">
                {translate("Requires Quote")}
            </Typography>
            <PortalTooltip text={siteMessage("Rfq_TooltipMessage").toString()} />
            {unitOfMeasure && (
                <Typography>&nbsp;/&nbsp;{unitOfMeasure}</Typography>
            )}
        </QuoteRow>
    </QuoteWrapper>
);

const MobileLabel: React.FC<{ label: string; helpContent?: any }> = ({ label, helpContent }) => (
    <span className="mobile-label">
        {helpContent ? (
            <ItemWithHelpInfoHelp title={label} helpContent={helpContent} />
        ) : (
            label
        )}
    </span>
);

const StyledDataTableRow = styled(DataTableRow)<{ $isDiscontinued?: boolean }>`

    &:nth-child(even) {
        background-color: white;
    }

    ${({ $isDiscontinued }) =>
        $isDiscontinued &&
        `
        opacity: 0.55;
        background-color: #f4f4f4 !important;
    `}

    .mobile-label {
        display: none;
    }

    @media (max-width: 767px) {
        display: block;
        border-bottom: 2px solid ${({ theme }) => theme.colors.secondary.contrast};

        td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 8px 0 0;
            border-bottom: 1px solid #6b6b6b;
            text-align: right;

            &:last-child {
                border-bottom: none;
            }

            &:not([data-has-label="true"]):before {
                content: attr(data-label);
                font-weight: bold;
                text-align: left;
                flex-shrink: 0;
                color: #071D49;
                background-color: ${({ theme }) => theme.colors.secondary.main};
                width: 50%;
                height: 100%;
                line-height: 1;
                padding: 8px;
                display: flex;
                align-items: center;
                white-space: normal;
                margin-right: 8px;
            }

            .mobile-label {
                display: flex;
                font-weight: bold;
                text-align: left;
                flex-shrink: 0;
                color: #071D49;
                background-color: ${({ theme }) => theme.colors.secondary.main};
                width: 50%;
                height: 100%;
                line-height: 1;
                padding: 8px;
                align-items: center;
                white-space: normal;

                svg {
                    margin-top: -10px;
                }
            }
        }
    }
`;

interface OwnProps {
    variantTraits: CustomVariantTraitModel[];
    showCADColumn: boolean;
    showCategoryDocumentColumn: boolean;
    isAuthenticated: boolean;
    showIncludesColumn: boolean;
    onLoginRequired: () => void;
    customRailModalConfig: CustomIncludeModalConfig;
}

type Props = OwnProps & HasProductContext;

const VariantTableRow = ({
    productContext,
    variantTraits,
    showCADColumn,
    showCategoryDocumentColumn,
    isAuthenticated,
    showIncludesColumn,
    onLoginRequired,
    customRailModalConfig,
}: Props) => {
    const variant = productContext.product;
    const productInfo = productContext.productInfo;

    const isDiscontinuedProduct = variant.properties?.[DISCONTINUED_BUT_VISIBLE] === "true";

    const CADDocument = React.useMemo(() => {
        if (!variant.documents?.length) {
            return undefined;
        }
        const docs = variant.documents.length > 1 ? [...variant.documents].sort(sortByDate) : variant.documents;
        return docs.find(
            doc =>
                doc.documentType?.toLowerCase() === DocumentTypeValues.Downloads.toLowerCase() &&
                doc.name.toLowerCase() === DocumentNameValues.CAD.toLocaleLowerCase(),
        );
    }, [variant.documents]);

    const CategoryDocument = React.useMemo(() => {
        if (!variant.documents?.length) {
            return undefined;
        }
        const docs = variant.documents.length > 1 ? [...variant.documents].sort(sortByDate) : variant.documents;
        return docs.find(doc => doc.documentType?.toLowerCase() === DocumentTypeValues.Catalog.toLowerCase());
    }, [variant.documents]);

    const daysToShipValue = variant?.attributeTypes
        ?.find(at => at.name.toLowerCase() === AttributeTypeNameValues.DaysToShip.toLowerCase())
            ?.attributeValues?.map(value => value.valueDisplay).join(", ") ?? "--";

    const inventoryAvailability = productInfo.inventory
        ?.inventoryAvailabilityDtos
        ?.find(dto => dto.unitOfMeasure === productInfo.unitOfMeasure || dto.unitOfMeasure === "");
    const isOutOfStock = inventoryAvailability?.availability?.messageType === AvailabilityMessageType.OutOfStock;
    
    const isCustomProduct = variant.properties[IS_CUSTOM_PRODUCT] === "true";
    const hasIncludes = variant.properties?.[HAS_INCLUDES_PRODUCTS] === "true";

    const getProductCategoryUrl = (product: ProductModel): string => {
        if (typeof document === "undefined") {
            return product.urlSegment;
        }

        const urlSegments = new URL(document.URL).pathname.split("/");
        const lastSegmentIndex = urlSegments.length - 1;
        urlSegments[lastSegmentIndex] = product.urlSegment;
        return urlSegments.join("/");
    }
    
    const handleDocumentClick = (e: React.MouseEvent) => {
        if (!isAuthenticated) {
            e.preventDefault();
            onLoginRequired();
        }
    };

    return (
        <StyledDataTableRow $isDiscontinued={isDiscontinuedProduct}>

            <DataTableCell className="frozen-items">

                    <StyledWrapper className="frozen-items__container">

                        <StyledWrapper data-label="Product Number">
                            {isCustomProduct ? (
                                <Typography>{variant.productTitle}</Typography>
                            ) : (
                                <Link href={getProductCategoryUrl(variant)}>{variant.productTitle}</Link>
                            )}
                        </StyledWrapper>
                        {showCADColumn && (
                            <StyledWrapper data-label="CAD">
                                {CADDocument ? (
                                    <Link
                                        href={CADDocument.filePath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={handleDocumentClick}                                        
                                    >
                                        <Icon src={Cad3D} title={isAuthenticated ? "CAD" : "Sign in to download"} size={24} />
                                    </Link>
                                ) : (
                                    <Typography>--</Typography>
                                )}
                            </StyledWrapper>
                        )}
                        {showCategoryDocumentColumn && (
                            <StyledWrapper data-label="Catalog">
                                {CategoryDocument ? (
                                    <Link
                                        href={CategoryDocument.filePath}
                                        target="_blank"
                                        rel="noopener noreferrer"                                        
                                    >
                                        <Icon src={Book} title="Catalog" size={24} />
                                    </Link>
                                ) : (
                                    <Typography>--</Typography>
                                )}
                            </StyledWrapper>
                        )}

                    </StyledWrapper>

            </DataTableCell>

            {isAuthenticated && (
                isCustomProduct ? 
                    (<DataTableCell data-label="Custom Rail">
                        <CustomRailSetupButton customRailModalConfig={customRailModalConfig} />
                    </DataTableCell>)
                :
                    (<DataTableCell data-label="Quantity">
                        {isDiscontinuedProduct ? (
                            <Typography>--</Typography>
                        ) : isOutOfStock ? (
                            <QuoteRow style={{ gap: "4px" }}>
                                <Typography weight="bold" color="danger">
                                    {inventoryAvailability?.availability?.message || translate("Out of stock")}
                                </Typography>
                                <PortalTooltip text={translate("Contact us to place an order")} />
                            </QuoteRow>
                        ) : (
                            <ProductQuantityOrdered />
                        )}
                    </DataTableCell>)
            )}
            {(showIncludesColumn && isAuthenticated) && (
                <DataTableCell data-label="Includes">
                    <Checkbox
                        checked={(productContext as VariantChildrenGridContextModel).addIncludes}
                        disabled={isOutOfStock || !hasIncludes}
                        onChange={(e, value) => {
                                    (productContext as VariantChildrenGridContextModel).onAddIncludesChanged?.(value);
                                }
                            }>
                    </Checkbox>
                </DataTableCell>
            )}            
            {isAuthenticated && (
                <DataTableCell data-label="Days to Ship">
                    {isOutOfStock || daysToShipValue?.toLowerCase().replace(/\s/g, "") === ProductDetailsEnum.ContactUs ? (
                        <Link href={`/ContactUs?productId=${variant.id}`}>{translate("Contact Us")}</Link>
                    ) : (
                        daysToShipValue
                    )}
                </DataTableCell>
            )}                
            {isAuthenticated && (
                <DataTableCell data-label="Price">
                    {isCustomProduct ? (
                        <Typography>--</Typography>
                    ) : variant.quoteRequired ? (
                        <RequiresQuoteCell
                            unitOfMeasure={
                                variant.unitOfMeasures?.find(
                                    o => o.unitOfMeasure === productInfo.unitOfMeasure,
                                )?.description ||
                                variant.unitOfMeasures?.find(
                                    o => o.unitOfMeasure === productInfo.unitOfMeasure,
                                )?.unitOfMeasureDisplay
                            }
                        />
                    ) : (
                        <ProductPrice
                            product={{ product: variant, productInfo }}
                            showLabel={false}
                        />
                    )}
                </DataTableCell>
            )}
            {variantTraits.map(trait => (
                !hideTrait(trait.customProperties) &&
                <DataTableCell key={trait.id} data-has-label="true">
                    <MobileLabel 
                        label={trait.nameDisplay || trait.name} 
                        helpContent={trait.customProperties}
                    />
                    <span>{getVariantTraitValueForProduct(variant, trait.id)}</span>
                </DataTableCell>
            ))}
        </StyledDataTableRow>
    );
};

export default withProductContext(React.memo(VariantTableRow));